import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { Check, ExternalLink } from "lucide-react";

import { Button } from "components/common";
import { Modal, ModalBody, ModalHeader } from "components/common/modal";
import { ChatGptLogoIcon } from "components/icons/ChatGptLogoIcon";
import { ClaudeLogoIcon } from "components/icons/ClaudeLogoIcon";
import { CursorLogoIcon } from "components/icons/CursorLogoIcon";
import { useSessionUser } from "hooks/useSession";
import { useWorkspaceAuth } from "hooks/useWorkspace/useWorkspaceAuth";
import type { McpConnectorClient } from "hooks/workspace/useMcpConnector";
import {
  connectionAppName,
  MCP_CAPABILITY_LABELS,
  MCP_CAPABILITY_SCOPES_IN_ORDER,
  useMcpConnections,
  type McpConnection,
} from "hooks/workspace/useMcpConnections";
import { Permissions } from "utils/permissions/permissionConstants";

import { AiAppMark, aiAppFromClientId } from "../AiAppMark/AiAppMark";
import {
  claudeAddUrl,
  CopyField,
  cursorInstallUrl,
  useCopy,
} from "../McpConnectorCard/McpConnectorCard";

const formatDate = (value: string): string => dayjs(value).format("MMM D, YYYY");

type Product = {
  key: McpConnectorClient;
  name: string;
  icon: ReactNode;
  /** Opens the app's own add flow. Absent when the app has no such link. */
  href?: (url: string) => string;
  /** Shown after the button copies the URL, for apps without a link. */
  hint?: string;
};

/**
 * One button per product. Claude covers Claude web, Desktop and Claude Code
 * (all use the same server URL); ChatGPT covers ChatGPT and Codex. Connections
 * are grouped by the host that published their client metadata.
 */
const PRODUCTS: Product[] = [
  {
    key: "claude",
    name: "Claude",
    icon: <ClaudeLogoIcon className="text-connector" />,
    href: claudeAddUrl,
  },
  {
    key: "chatgpt",
    name: "ChatGPT",
    icon: <ChatGptLogoIcon />,
    // ChatGPT has no link into its app setup, so the button copies the URL instead.
    hint: "Server URL copied. In ChatGPT, turn on developer mode, then create an app with it.",
  },
  {
    key: "cursor",
    name: "Cursor",
    icon: <CursorLogoIcon />,
    href: cursorInstallUrl,
  },
];

const Eyebrow = ({ children }: { children: ReactNode }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
    {children}
  </p>
);

const LinkProductButton = ({
  product,
  url,
}: {
  product: Product & { href: NonNullable<Product["href"]> };
  url: string;
}) => (
  <Button asChild variant="subtle" size="sm">
    <a href={product.href(url)} target="_blank" rel="noopener noreferrer">
      {product.icon}
      {product.name}
      <ExternalLink className="h-3 w-3 text-muted-foreground" aria-hidden />
    </a>
  </Button>
);

const CopyProductButton = ({
  product,
  url,
  onCopied,
}: {
  product: Product;
  url: string;
  onCopied: () => void;
}) => {
  const { copied, copy } = useCopy(url);
  return (
    <Button
      type="button"
      variant="subtle"
      size="sm"
      title={url}
      aria-label={`Copy server URL for ${product.name}`}
      onClick={() => {
        void copy();
        onCopied();
      }}
    >
      {copied ? <Check className="text-presence" aria-hidden /> : product.icon}
      {copied ? "Copied" : product.name}
    </Button>
  );
};

/** One button per product. The parent shows any hint below. */
const ProductButtons = ({
  url,
  onCopied,
}: {
  url: string;
  onCopied: (product: McpConnectorClient) => void;
}) => (
  <div className="flex flex-wrap items-center gap-2">
    {PRODUCTS.map((product) =>
      product.href ? (
        <LinkProductButton
          key={product.key}
          product={{ ...product, href: product.href }}
          url={url}
        />
      ) : (
        <CopyProductButton
          key={product.key}
          product={product}
          url={url}
          onCopied={() => onCopied(product.key)}
        />
      )
    )}
  </div>
);

/** The add buttons. Sits beside the server URL; the parent shows any hint below the row. */
const AddButtons = ({
  url,
  onCopied,
}: {
  url: string;
  onCopied: (product: McpConnectorClient) => void;
}) => (
  <div className="flex flex-col gap-1.5">
    <Eyebrow>Add RivetAI to</Eyebrow>
    <ProductButtons url={url} onCopied={onCopied} />
  </div>
);

/**
 * One-line promo for the Projects page: the same add buttons as the
 * Connections page, plus Dismiss. Hidden once the signed-in user has
 * connected any AI app, and while that is still loading.
 */
export const AiAppsPromo = ({
  workspaceId,
  url,
  onDismiss,
}: {
  workspaceId: string;
  url: string;
  onDismiss: () => void;
}) => {
  const { connectionsQuery } = useMcpConnections(workspaceId, "mine");
  const [hintFor, setHintFor] = useState<McpConnectorClient | null>(null);
  const hint = PRODUCTS.find((product) => product.key === hintFor)?.hint;

  // A failed list is unknown, not empty: the user may already be connected.
  const connectionsLoadFailed = connectionsQuery.isError && connectionsQuery.data === undefined;
  if (
    connectionsQuery.isLoading ||
    connectionsLoadFailed ||
    (connectionsQuery.data ?? []).length > 0
  ) {
    return null;
  }

  return (
    <section
      aria-label="Add RivetAI to an AI app"
      className="flex flex-col gap-1.5 rounded-lg border border-border bg-card px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="text-sm font-medium text-foreground">
          Run breakdowns, schedules, and budgets from your AI app
        </p>
        <ProductButtons url={url} onCopied={setHintFor} />
        <Button type="button" variant="ghost" size="sm" onClick={onDismiss} className="ml-auto">
          Dismiss
        </Button>
      </div>
      {hint ? (
        <p className="text-xs text-muted-foreground" role="status">
          {hint}
        </p>
      ) : null}
    </section>
  );
};

/**
 * The header's "Connect" dialog: the same add buttons as the Connections
 * page, and a link there for managing what is connected.
 */
export const AiAppsConnectDialog = ({
  isOpen,
  onClose,
  url,
  connectionsPath,
}: {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  connectionsPath: string;
}) => {
  const [hintFor, setHintFor] = useState<McpConnectorClient | null>(null);
  const hint = PRODUCTS.find((product) => product.key === hintFor)?.hint;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="max-w-md">
      <ModalHeader
        title="Connect an AI app"
        subtitle="Run breakdowns, schedules, and budgets from a chat in your AI app."
        onClose={onClose}
      />
      <ModalBody showBorders={false}>
        <div className="flex flex-col gap-3">
          <ProductButtons url={url} onCopied={setHintFor} />
          {hint ? (
            <p className="text-xs text-muted-foreground" role="status">
              {hint}
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            <Link
              to={connectionsPath}
              onClick={onClose}
              className="underline underline-offset-2 hover:text-foreground"
            >
              Manage connections
            </Link>{" "}
            in workspace settings.
          </p>
        </div>
      </ModalBody>
    </Modal>
  );
};

/** Connections of one person for one app, shown and revoked as a single row. */
type ConnectionGroup = {
  key: string;
  product: McpConnectorClient | null;
  name: string;
  userId: string;
  connections: McpConnection[];
};

const groupConnections = (connections: McpConnection[]): ConnectionGroup[] => {
  const groups = new Map<string, ConnectionGroup>();
  for (const connection of connections) {
    const product = aiAppFromClientId(connection.client_id);
    const name = product
      ? (PRODUCTS.find((p) => p.key === product)?.name ?? product)
      : connectionAppName(connection);
    const key = `${product ?? name}|${connection.user_id}`;
    const group = groups.get(key);
    if (group) group.connections.push(connection);
    else
      groups.set(key, {
        key,
        product,
        name,
        userId: connection.user_id,
        connections: [connection],
      });
  }
  return [...groups.values()];
};

const ownerName = (connection: McpConnection): string | null => {
  const owner = connection.owner;
  if (!owner) return null;
  const name = `${owner.first_name ?? ""} ${owner.last_name ?? ""}`.trim();
  return name || owner.email || null;
};

/**
 * AI apps for a workspace: the server URL, one add button per product, and the
 * signed-in user's connections with their permissions and Disconnect. The add
 * buttons stay while connected, since one product can be added to more than
 * one client (Claude web and Claude Code, Cursor on two machines). Workspace
 * admins also see teammates' connections, labeled with the name.
 */
export const AiAppsSection = ({ workspaceId, url }: { workspaceId: string; url: string }) => {
  const { canWorkspace } = useWorkspaceAuth(workspaceId);
  const isAdmin = canWorkspace(Permissions.WORKSPACE_API_KEY_MANAGE);
  // The signed-in user from the session. The legacy auth store has no user ID
  // under Better Auth, which made every connection look like a teammate's.
  const { session } = useSessionUser();
  const currentUserId = session?.user_id ?? null;
  const { connectionsQuery, revokeMutation } = useMcpConnections(
    workspaceId,
    isAdmin ? "all" : "mine"
  );
  const [confirming, setConfirming] = useState<string | null>(null);
  // One hint at a time, for the last copy-only app used.
  const [hintFor, setHintFor] = useState<McpConnectorClient | null>(null);
  const hint = PRODUCTS.find((product) => product.key === hintFor)?.hint;

  // Wait for the session so a person's own connection is never labeled as a teammate's.
  const ready = Boolean(currentUserId) && !connectionsQuery.isLoading;
  // A failed refetch keeps the loaded list, so only a first load with nothing to show fails.
  const connectionsLoadFailed = connectionsQuery.isError && connectionsQuery.data === undefined;
  const groups = ready ? groupConnections(connectionsQuery.data ?? []) : [];
  // Own connections first, in product order, then teammates', then unknown apps.
  const rank = (group: ConnectionGroup) =>
    (group.userId === currentUserId ? 0 : 10) +
    (group.product ? PRODUCTS.findIndex((product) => product.key === group.product) : 9);
  const ordered = [...groups].sort((a, b) => rank(a) - rank(b));

  const renderGroup = (group: ConnectionGroup) => {
    const grantIds = group.connections.map((connection) => connection.grant_id);
    return (
      <ConnectedRow
        key={group.key}
        group={group}
        owner={
          !currentUserId || group.userId === currentUserId ? null : ownerName(group.connections[0])
        }
        confirming={confirming === group.key}
        isRevoking={
          revokeMutation.isPending &&
          (revokeMutation.variables ?? []).some((grantId) => grantIds.includes(grantId))
        }
        onAskRevoke={() => setConfirming(group.key)}
        onCancel={() => setConfirming(null)}
        onRevoke={() => revokeMutation.mutate(grantIds, { onSettled: () => setConfirming(null) })}
      />
    );
  };

  return (
    <section aria-labelledby="ai-apps-heading" className="flex flex-col gap-3">
      <h2 id="ai-apps-heading" className="text-base font-semibold text-foreground">
        AI apps
      </h2>

      {/* URL and buttons share a row; the buttons wrap below on narrow screens. */}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div className="min-w-[18rem] max-w-xl flex-1">
            <CopyField label="Server URL" value={url} />
          </div>
          <AddButtons url={url} onCopied={setHintFor} />
        </div>
        {hint ? (
          <p className="text-xs text-muted-foreground" role="status">
            {hint}
          </p>
        ) : null}
      </div>

      {connectionsLoadFailed ? (
        <div
          role="alert"
          className="mt-1 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
        >
          <p className="text-sm text-foreground">Couldn't load connected AI apps</p>
          <Button
            type="button"
            variant="badge"
            color="gray"
            size="sm"
            onClick={() => void connectionsQuery.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : ordered.length > 0 ? (
        <div className="mt-1 flex flex-col gap-1.5">
          <Eyebrow>Connected</Eyebrow>
          <ul className="divide-y divide-border rounded-lg border border-border bg-card">
            {ordered.map(renderGroup)}
          </ul>
        </div>
      ) : null}
    </section>
  );
};

const Row = ({
  mark,
  name,
  tags,
  detail,
  actions,
}: {
  mark: ReactNode;
  name: string;
  tags?: ReactNode;
  detail?: ReactNode;
  actions: ReactNode;
}) => (
  <li className="flex items-center justify-between gap-3 px-4 py-3">
    <div className="flex min-w-0 items-center gap-3">
      {mark}
      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{name}</span>
          {tags}
        </div>
        {detail ? <span className="text-xs text-muted-foreground">{detail}</span> : null}
      </div>
    </div>
    <div className="flex shrink-0 items-center gap-2">{actions}</div>
  </li>
);

const ConnectedRow = ({
  group,
  owner,
  confirming,
  isRevoking,
  onAskRevoke,
  onCancel,
  onRevoke,
}: {
  group: ConnectionGroup;
  owner: string | null;
  confirming: boolean;
  isRevoking: boolean;
  onAskRevoke: () => void;
  onCancel: () => void;
  onRevoke: () => void;
}) => {
  const scopes = new Set(group.connections.flatMap((connection) => connection.scopes));
  const permissions = MCP_CAPABILITY_SCOPES_IN_ORDER.filter((scope) => scopes.has(scope)).map(
    (scope) => MCP_CAPABILITY_LABELS[scope]
  );
  const lastUsed = group.connections
    .map((connection) => connection.last_used_at)
    .filter((value): value is string => Boolean(value))
    .sort()
    .pop();

  return (
    <Row
      mark={<AiAppMark client={group.product} fallbackName={group.name} />}
      name={group.name}
      tags={permissions.map((permission) => (
        <span
          key={permission}
          className="rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
        >
          {permission}
        </span>
      ))}
      detail={
        <>
          {owner ? `${owner} · ` : ""}
          {lastUsed ? `Last used ${formatDate(lastUsed)}` : "Not used yet"}
        </>
      }
      actions={
        confirming ? (
          <>
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isRevoking}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={onRevoke} isLoading={isRevoking}>
              Disconnect
            </Button>
          </>
        ) : (
          <Button variant="ghost" size="sm" onClick={onAskRevoke}>
            Disconnect
          </Button>
        )
      }
    />
  );
};
